import React, { Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ToastContainer from './components/feedback/ToastContainer';
import Loading from './components/feedback/Loading';
import ErrorBoundary from './components/feedback/ErrorBoundary';
import NetworkStatus from './components/common/NetworkStatus';
import { useUIStore } from './store/uiStore';
import { useMobile, useNetworkStatus } from './hooks/useMobile';
import './i18n'; // 引入i18n配置
import { MemberService } from './api/services/MemberService';
import { useUserStore } from './store/userStore';
import RechargeRecord from './pages/RechargeRecord';
import ChangePassword from './pages/ChangePassword';

// 路由懒加载 - 性能优化
const Home = React.lazy(() => import('./pages/Home'));
const Products = React.lazy(() => import('./pages/Products'));
const ProductDetail = React.lazy(() => import('./pages/ProductDetail'));
const Lottery = React.lazy(() => import('./pages/Lottery'));
const Orders = React.lazy(() => import('./pages/Orders'));
const OrderDetail = React.lazy(() => import('./pages/OrderDetail'));
const PointsDetail = React.lazy(() => import('./pages/PointsDetail'));
const Recharge = React.lazy(() => import('./pages/Recharge'));
const Profile = React.lazy(() => import('./pages/Profile'));
const MyNumbers = React.lazy(() => import('./pages/MyNumbers'));
const MyPrizes = React.lazy(() => import('./pages/MyPrizes'));
const EditProfile = React.lazy(() => import('./pages/EditProfile'));
const LotteryResult = React.lazy(() => import('./pages/LotteryResult'));
const LotteryNoPrize = React.lazy(() => import('./pages/LotteryNoPrize'));
const LotteryRules = React.lazy(() => import('./pages/LotteryRules'));
const AddressManage = React.lazy(() => import('./pages/AddressManage'));
const Settings = React.lazy(() => import('./pages/Settings'));
const HelpCenter = React.lazy(() => import('./pages/HelpCenter'));
const Notifications = React.lazy(() => import('./pages/Notifications'));
const CheckIn = React.lazy(() => import('./pages/CheckIn'));
const Invite = React.lazy(() => import('./pages/Invite'));
const Login = React.lazy(() => import('./pages/Login'));
const Register = React.lazy(() => import('./pages/Register'));
const Favorite = React.lazy(() => import('./pages/Favorite'));

// function App() {
// 	const { t } = useTranslation();
// 	const { toasts, removeToast, isLoading, addToast } = useUIStore();
// 	const mobileInfo = useMobile();
// 	const { isOnline, connectionType } = useNetworkStatus();
// 	const { login: loginUser, setUser } = useUserStore();
// 	const location = useLocation();
// 	const getCurrentMember = async () => {
// 		try {
// 			const res = await MemberService.currentMember();
// 			if (res) {
// 				// loginUser(res.user_info, res.access_token);
// 				setUser(res.userInfo);
// 			}
// 		} catch (err) {
// 			setUser(null);
// 		}
// 	};

// 	// 监听网络状态变化
// 	useEffect(() => {
// 		if (!isOnline) {
// 			addToast({
// 				id: 'offline',
// 				type: 'warning',
// 				message: t('messages.networkError'),
// 				duration: 5000,
// 			});
// 		}
// 	}, [isOnline, addToast, t]);

// 	// 为移动端添加CSS类名
// 	useEffect(() => {
// 		const html = document.documentElement;
// 		html.classList.toggle('is-mobile', mobileInfo.isMobile);
// 		html.classList.toggle('is-ios', mobileInfo.isIOS);
// 		html.classList.toggle('is-android', mobileInfo.isAndroid);
// 		html.classList.toggle('is-wechat', mobileInfo.isWeChat);
// 		html.classList.toggle('is-pwa', mobileInfo.isPWA);
// 		html.classList.toggle('is-landscape', mobileInfo.orientation === 'landscape');
// 	}, [mobileInfo]);
// 	useEffect(() => {
// 		getCurrentMember();
// 	}, [location.pathname]);
// 	return (
// 		<ErrorBoundary>
// 			<BrowserRouter>
// 				<NetworkStatus />
// 				{isLoading && <Loading fullScreen />}
// 				<ToastContainer toasts={toasts} onRemove={removeToast} />
// 				<Suspense fallback={<Loading fullScreen />}>
// 					<Routes>
// 						<Route path="/" element={<Home />} />
// 						<Route path="/products" element={<Products />} />
// 						<Route path="/product/:id" element={<ProductDetail />} />
// 						<Route path="/lottery" element={<Lottery />} />
// 						<Route path="/orders" element={<Orders />} />
// 						<Route path="/order-detail/:id" element={<OrderDetail />} />
// 						<Route path="/points-detail" element={<PointsDetail />} />
// 						<Route path="/recharge" element={<Recharge />} />
// 						<Route path="/profile" element={<Profile />} />
// 						<Route path="/my-numbers" element={<MyNumbers />} />
// 						<Route path="/my-prizes" element={<MyPrizes />} />
// 						<Route path="/edit-profile" element={<EditProfile />} />
// 						<Route path="/lottery-result" element={<LotteryResult />} />
// 						<Route path="/lottery-no-prize" element={<LotteryNoPrize />} />
// 						<Route path="/lottery-rules" element={<LotteryRules />} />
// 						<Route path="/address-manage" element={<AddressManage />} />
// 						<Route path="/settings" element={<Settings />} />
// 						<Route path="/help-center" element={<HelpCenter />} />
// 						<Route path="/notifications" element={<Notifications />} />
// 						<Route path="/check-in" element={<CheckIn />} />
// 						<Route path="/invite" element={<Invite />} />
// 						<Route path="/login" element={<Login />} />
// 						<Route path="/register" element={<Register />} />
// 						<Route path="/favorite" element={<Favorite />} />
// 					</Routes>
// 				</Suspense>
// 			</BrowserRouter>
// 		</ErrorBoundary>
// 	);
// }

// export default App;
function AppContent() {
	const { t } = useTranslation();
	const { toasts, removeToast, isLoading, addToast } = useUIStore();
	const mobileInfo = useMobile();
	const { isOnline } = useNetworkStatus();
	const { setUser } = useUserStore();
	const location = useLocation();
	const navigate = useNavigate();

	const getCurrentMember = async () => {
		try {
			const res = await MemberService.currentMember();
			if (res) {
				setUser(res.userInfo);
			}
		} catch (err) {
			setUser(null);
			navigate('/login');
		}
	};

	// ✅ 初始加载时执行一次
	// useEffect(() => {
	// 	getCurrentMember();
	// }, []);

	// ✅ 路由变化时再执行
	useEffect(() => {
		if (
			location.pathname !== '/login' &&
			location.pathname !== '/register' &&
			location.pathname !== '/change-password'
		) {
			getCurrentMember();
		}
	}, [location.pathname]);

	// 网络状态监听
	useEffect(() => {
		if (!isOnline) {
			addToast({
				id: 'offline',
				type: 'warning',
				message: t('messages.networkError'),
				duration: 5000,
			});
		}
	}, [isOnline, addToast, t]);

	// 移动端样式 className
	useEffect(() => {
		const html = document.documentElement;
		html.classList.toggle('is-mobile', mobileInfo.isMobile);
		html.classList.toggle('is-ios', mobileInfo.isIOS);
		html.classList.toggle('is-android', mobileInfo.isAndroid);
		html.classList.toggle('is-wechat', mobileInfo.isWeChat);
		html.classList.toggle('is-pwa', mobileInfo.isPWA);
		html.classList.toggle('is-landscape', mobileInfo.orientation === 'landscape');
	}, [mobileInfo]);

	return (
		<>
			<NetworkStatus />
			{isLoading && <Loading fullScreen />}
			<ToastContainer toasts={toasts} onRemove={removeToast} />
			<Suspense fallback={<Loading fullScreen />}>
				<Routes>
					<Route path="/" element={<Home />} />
					<Route path="/products" element={<Products />} />
					<Route path="/product/:id" element={<ProductDetail />} />
					<Route path="/lottery" element={<Lottery />} />
					<Route path="/orders" element={<Orders />} />
					<Route path="/order-detail/:id" element={<OrderDetail />} />
					<Route path="/points-detail" element={<PointsDetail />} />
					<Route path="/recharge" element={<Recharge />} />
					<Route path="/profile" element={<Profile />} />
					<Route path="/my-numbers" element={<MyNumbers />} />
					<Route path="/my-prizes" element={<MyPrizes />} />
					<Route path="/edit-profile" element={<EditProfile />} />
					<Route path="/lottery-result" element={<LotteryResult />} />
					<Route path="/lottery-no-prize" element={<LotteryNoPrize />} />
					<Route path="/lottery-rules" element={<LotteryRules />} />
					<Route path="/address-manage" element={<AddressManage />} />
					<Route path="/settings" element={<Settings />} />
					<Route path="/help-center" element={<HelpCenter />} />
					<Route path="/notifications" element={<Notifications />} />
					<Route path="/check-in" element={<CheckIn />} />
					<Route path="/invite" element={<Invite />} />
					<Route path="/login" element={<Login />} />
					<Route path="/register" element={<Register />} />
					<Route path="/favorite" element={<Favorite />} />
					<Route path="/recharge-record" element={<RechargeRecord />} />
					<Route path="/change-password" element={<ChangePassword />} />
				</Routes>
			</Suspense>
		</>
	);
}

export default function App() {
	return (
		<ErrorBoundary>
			<BrowserRouter>
				<AppContent />
			</BrowserRouter>
		</ErrorBoundary>
	);
}
